import { supabase } from './supabase'
import { env } from './env'

export type EnrichmentProvider = 'harmonic'

export interface TeamEnrichmentStatus {
  teamId: string
  provider: EnrichmentProvider
  canManage: boolean
  connected: boolean
  status: 'active' | 'invalid' | 'disabled' | 'not_configured'
  keyLast4: string | null
  configuredBy: string | null
  verifiedAt: string | null
  disabledAt: string | null
  updatedAt: string | null
}

export interface ExtensionEnrichmentCapability {
  token: string
  expiresAt: number
  teamId: string
  provider: EnrichmentProvider
  functionsBaseUrl: string
  functionsAnonKey: string
}

export type MintCapabilityResult =
  | ExtensionEnrichmentCapability
  | { token: null; reason: 'not_configured'; teamId: string; provider: EnrichmentProvider }

function errorMessage(value: unknown): string {
  if (typeof value === 'string') return value
  if (value instanceof Error) return value.message
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    if (typeof record.message === 'string') return record.message
    if (typeof record.error === 'string') return record.error
    try {
      return JSON.stringify(value)
    } catch {
      return 'Unexpected function error'
    }
  }
  return value == null ? 'Unexpected function error' : String(value)
}

async function authedFunctionFetch<T>(
  functionName: string,
  init: Omit<RequestInit, 'body'> & { body?: BodyInit | Record<string, unknown> } = {},
): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Not authenticated')

  const headers = new Headers(init.headers)
  headers.set('Authorization', `Bearer ${session.access_token}`)
  headers.set('apikey', env.supabase.anonKey)
  if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const rawBody =
    init.body && !(typeof init.body === 'string') && !(init.body instanceof FormData)
      ? JSON.stringify(init.body)
      : init.body

  const response = await fetch(`${env.supabase.url.replace(/\/$/, '')}/functions/v1/${functionName}`, {
    ...init,
    headers,
    body: rawBody as BodyInit | undefined,
  })

  const contentType = response.headers.get('content-type') || ''
  const payload = contentType.includes('application/json')
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null)

  if (!response.ok) {
    const message =
      payload && typeof payload === 'object' && 'error' in payload
        ? errorMessage((payload as { error?: unknown }).error)
        : `Function ${functionName} failed`
    throw new Error(message)
  }

  return payload as T
}

export function getTeamEnrichmentStatus(teamId: string, provider: EnrichmentProvider = 'harmonic') {
  const query = new URLSearchParams({ teamId, provider })
  return authedFunctionFetch<TeamEnrichmentStatus>(`team-enrichment-config?${query}`, {
    method: 'GET',
  })
}

export function setTeamEnrichmentApiKey(params: {
  teamId: string
  provider?: EnrichmentProvider
  apiKey: string
}) {
  return authedFunctionFetch<TeamEnrichmentStatus>('team-enrichment-config', {
    method: 'PUT',
    body: {
      teamId: params.teamId,
      provider: params.provider ?? 'harmonic',
      apiKey: params.apiKey,
    },
  })
}

export function disableTeamEnrichment(teamId: string, provider: EnrichmentProvider = 'harmonic') {
  return authedFunctionFetch<TeamEnrichmentStatus>('team-enrichment-config', {
    method: 'DELETE',
    body: { teamId, provider },
  })
}

export function mintExtensionEnrichmentCapability(
  teamId: string,
  provider: EnrichmentProvider = 'harmonic',
) {
  return authedFunctionFetch<MintCapabilityResult>('extension-enrichment-token', {
    method: 'POST',
    body: { teamId, provider },
  })
}
